import { useEffect, useMemo, useState } from "react";
import {
  TableColumns,
  TableColumnWidth,
  TableData,
  TextBox,
} from "@veneer/core";
import { useTranslation } from "react-i18next";
import userService from "../../services/generalApi/user.service";
import { IModalMessage } from "../../models/dto/generalApi/ICheckInVsAbsence";

export const UseCheckInVsAbsenceSummary = (
  selectedMonth: number,
  selectedYear: number,
  setLoadingCheckInVsAbsenceSummary: (loading: boolean) => void,
  setModalMessage: React.Dispatch<React.SetStateAction<IModalMessage>>
) => {
  const [originalDataSummary, setOriginalDataSummary] = useState<TableData[]>(
    []
  );
  const [searchUser, setSearchUser] = useState<string>("");
  const [debouncedSearchUser, setDebouncedSearchUser] =
    useState<string>(searchUser);
  const [currentPageSummary, setCurrentPageSummary] = useState(1);
  const [pageSizeSummary, setPageSizeSummary] = useState<number>(10);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const currentMonthSummary = new Date().getMonth() + 1;
  const currentYearSummary = new Date().getFullYear();

  const [t] = useTranslation("global");

  const yearsSummary = Array.from(
    { length: 2 },
    (_, i) => currentYearSummary - i
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthsSummary = [
    { value: 1, label: t("header.january") },
    { value: 2, label: t("header.february") },
    { value: 3, label: t("header.march") },
    { value: 4, label: t("header.april") },
    { value: 5, label: t("header.may") },
    { value: 6, label: t("header.june") },
    { value: 7, label: t("header.july") },
    { value: 8, label: t("header.august") },
    { value: 9, label: t("header.september") },
    { value: 10, label: t("header.october") },
    { value: 11, label: t("header.november") },
    { value: 12, label: t("header.december") },
  ];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchUser(searchUser);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchUser]);

  const filteredMonthsSummary = useMemo(() => {
    if (selectedYear === currentYearSummary) {
      return monthsSummary.filter(
        (monthSummary) => monthSummary.value <= currentMonthSummary
      );
    }
    return monthsSummary;
  }, [selectedYear, currentYearSummary, currentMonthSummary, monthsSummary]);

  const handleChangeSearchUser = (value: string) => {
    setSearchUser(value);
    setCurrentPageSummary(1);
  };

  const searchTextBox = useMemo(
    () => (
      <TextBox
        className="searchUserCheckInVsAbsence"
        id="TextboxUser"
        label={t("header.search")}
        type="search"
        onChange={handleChangeSearchUser}
        value={searchUser}
      />
    ),
    [searchUser, t]
  );

  const columnsSummary: TableColumns[] = [
    { id: "nameOfUser", label: t("header.name"), filter: searchTextBox },
    { id: "earlyCheckIn", label: t("header.earlyCheckIn") },
    { id: "lateCheckIn", label: t("header.lateCheckIn") },
    { id: "justifiedAbsence", label: t("header.justifiedAbsence") },
    { id: "scheduledDayOff", label: t("header.scheduledDayOff") },
    { id: "unjustifiedAbsence", label: t("header.unjustifiedAbsence") },
    {
      id: "hasNoCheckInOrAbsence",
      label: t("header.noScheduledAgendaOrAbsence"),
    },
    { id: "totalDaysMonth", label: t("header.totalDaysMonth") },
  ];

  const columnsWidthSummary: TableColumnWidth[] = [
    { columnId: "nameOfUser", width: "160px" },
    { columnId: "earlyCheckIn", width: "150px" },
    { columnId: "lateCheckIn", width: "130px" },
    { columnId: "justifiedAbsence", width: "160px" },
    { columnId: "scheduledDayOff", width: "130px" },
    { columnId: "unjustifiedAbsence", width: "180px" },
    { columnId: "hasNoCheckInOrAbsence", width: "180px" },
    { columnId: "totalDaysMonth", width: "130px" },
  ];

  useEffect(() => {
    getData(selectedYear, selectedMonth);
  }, [selectedMonth, selectedYear]);

  const capitalizeName = (name: string) => {
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getData = async (Year: number, Month: number) => {
    setOriginalDataSummary([]);
    setLoadingSummary(true);
    try {
      const response = await userService.checkInVsAbsenceSummary(Year, Month);

      const formattedResponse = response.map((item: any) => ({
        ...item,
        nameOfUser: capitalizeName(item.nameOfUser),
      }));

      setOriginalDataSummary(formattedResponse);
    } catch (error) {
      console.error("Error:", error);
    }
    setLoadingSummary(false);
  };

  async function onClickCheckInVsAbsenceSummary(
    year: number,
    month: number,
    languageId: number
  ): Promise<void> {
    setLoadingCheckInVsAbsenceSummary(true);
    try {
      const result = await userService.checkInVsAbsenceSummaryExcel(
        year,
        month,
        languageId
      );
      if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "checkInVsAbsenceSummary.xlsx");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        setModalMessage({
          show: true,
          message: t("header.unexpectedResponse"),
          title: t("header.error"),
          onClose: () =>
            setModalMessage((prevState) => ({ ...prevState, show: false })),
        });
      }
    } catch (error) {
      setModalMessage({
        show: true,
        message: t("header.errorDownloadingFile"),
        title: t("header.error"),
        onClose: () =>
          setModalMessage((prevState) => ({ ...prevState, show: false })),
      });
    } finally {
      setLoadingCheckInVsAbsenceSummary(false);
    }
  }

  const tableDataSummary = useMemo(() => {
    if (!debouncedSearchUser) return originalDataSummary;

    return originalDataSummary.filter((element) =>
      element.nameOfUser
        .toLowerCase()
        .includes(debouncedSearchUser.toLowerCase())
    );
  }, [debouncedSearchUser, originalDataSummary]);

  const pagedDataSummary = useMemo(() => {
    return tableDataSummary.slice(
      (currentPageSummary - 1) * pageSizeSummary,
      currentPageSummary * pageSizeSummary
    );
  }, [tableDataSummary, currentPageSummary, pageSizeSummary]);

  return {
    columnsSummary,
    columnsWidthSummary,
    tableDataSummary,
    pagedDataSummary,
    currentPageSummary,
    pageSizeSummary,
    loadingSummary,
    setCurrentPageSummary,
    setPageSizeSummary,
    filteredMonthsSummary,
    yearsSummary,
    onClickCheckInVsAbsenceSummary,
    originalDataSummary,
  };
};
